import React, { useEffect, useRef, useState } from "react";
import PContent from "../text/PContent";
import classNames from "classnames";

type ButtonVariant = "validation" | "cancel" | "negatif";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  expandWidth?: boolean; // Si true, le bouton s'adapte à la largeur du texte
  /**
   * Avec `variant="cancel"` :
   * - `true` : rouge (déconnexion pro/patient)
   * - `"light"` : contour et texte blancs (sidebar sombre, ex. établissement)
   */
  cancelCustomColor?: boolean | "light";
}

const Button: React.FC<ButtonProps> = ({
  children,
  className,
  variant = "validation",
  expandWidth = false,
  cancelCustomColor = false,
  type = "button",
  style,
  onMouseDown,
  onMouseUp,
  onMouseLeave,
  onTouchStart,
  onTouchEnd,
  onTouchCancel,
  ...props
}) => {
  const [size, setSize] = useState({ width: 150, height: 39 });
  const [pressed, setPressed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    const updateSize = () => {
      const screenWidth = window.innerWidth;
      const WidthMobileM = 120;
      const ScreenWidthMobileM = 320;

      const WidthKscreen = 233;
      const ScreenWidthKscreen = 2560;

      const a = (WidthKscreen - WidthMobileM) / (ScreenWidthKscreen - ScreenWidthMobileM);
      const b = WidthMobileM - (a * ScreenWidthMobileM);

      const width = a * screenWidth + b;
      const height = 0.26 * width; // ratio réduit pour une hauteur plus basse

      setSize({ width, height });
      setIsMobile(screenWidth < 1024);
    };

    updateSize();
    window.addEventListener("resize", updateSize);
    return () => {
      window.removeEventListener("resize", updateSize);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  // Styles spécifiques par variant (inversion vert ↔ blanc : transitions simples sur fond + texte)
  const getVariantStyles = () => {
    switch (variant) {
      case "validation":
        return {
          baseClasses: `group mt-6 rounded-lg
            font-sans text-nowrap font-bold px-6 py-1.5
            bg-primary border-2 border-primary
            laptop:rounded-xl shadow-custom transition-all duration-500
            hover:bg-white active:bg-white hover:shadow-custom-lg
            flex justify-center items-center`,
          hasScale: true,
          innerTextClass:
            "text-center text-white transition-colors duration-500 group-hover:text-primary group-active:text-primary",
        };

      case "cancel":
        if (cancelCustomColor === "light") {
          return {
            baseClasses: `group mt-6 rounded-lg text-center
              font-sans text-nowrap font-bold px-6 py-1.5 flex flex-row
              justify-center items-center bg-transparent border-2 border-white
              laptop:rounded-xl tablet:rounded-xl shadow-custom transition-all duration-500
              hover:bg-white/15 active:bg-white/25 hover:shadow-custom-lg`,
            hasScale: true,
            innerTextClass:
              "text-center text-white transition-colors duration-500 group-hover:text-white",
          };
        }
        if (cancelCustomColor === true) {
          return {
            baseClasses: `group mt-6 rounded-lg text-center
              font-sans text-nowrap font-bold px-6 py-1.5 flex flex-row
              justify-center items-center bg-white border-2 border-red-600
              laptop:rounded-xl tablet:rounded-xl shadow-custom transition-all duration-500
              hover:bg-red-600 active:bg-red-600 hover:shadow-custom-lg`,
            hasScale: true,
            innerTextClass:
              "text-center text-red-600 transition-colors duration-500 group-hover:text-white group-active:text-white",
          };
        }
        return {
          baseClasses: `group mt-6 rounded-lg text-center
            font-sans text-nowrap font-bold px-6 py-1.5 flex flex-row
            justify-center items-center bg-white border-2 border-primary
            laptop:rounded-xl tablet:rounded-xl shadow-custom transition-all duration-500
            hover:bg-primary active:bg-primary hover:shadow-custom-lg`,
          hasScale: true,
          innerTextClass:
            "text-center text-primary transition-colors duration-500 group-hover:text-white group-active:text-white",
        };

      case "negatif":
        return {
          baseClasses: `group mt-6 rounded-lg
            text-center text-opacity-80 
            font-sans text-nowrap font-bold px-6 py-1.5 flex flex-row 
            justify-center items-center bg-white border-2 border-secondary 
            laptop:rounded-xl tablet:rounded-xl shadow-custom transition-colors 
            duration-200 hover:bg-secondary active:bg-secondary hover:shadow-custom-lg`,
          hasScale: true,
          innerTextClass:
            "text-center text-secondary transition-colors duration-200 group-hover:text-white group-active:text-white",
        };

      default:
        return {
          baseClasses: `group mt-6 rounded-lg
            font-sans text-nowrap font-bold px-6 py-1.5
            bg-primary border-2 border-primary
            laptop:rounded-xl shadow-custom transition-all duration-500
            hover:bg-white active:bg-white hover:shadow-custom-lg
            flex justify-center items-center`,
          hasScale: true,
          innerTextClass:
            "text-center text-white transition-colors duration-500 group-hover:text-primary group-active:text-primary",
        };
    }
  };

  const variantStyles = getVariantStyles();

  const resetPressed = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
    setPressed(false);
  };

  const setTemporaryPressed = () => {
    setPressed(true);
    timeoutRef.current = setTimeout(() => setPressed(false), 1000);
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLButtonElement>) => {
    onMouseDown?.(e);
    setTemporaryPressed();
  };

  const handleMouseUp = (e: React.MouseEvent<HTMLButtonElement>) => {
    onMouseUp?.(e);
    resetPressed();
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
    onMouseLeave?.(e);
    resetPressed();
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLButtonElement>) => {
    onTouchStart?.(e);
    if (isMobile) setTemporaryPressed();
  };

  const handleTouchEnd = (e: React.TouchEvent<HTMLButtonElement>) => {
    onTouchEnd?.(e);
    if (isMobile) resetPressed();
  };

  const handleTouchCancel = (e: React.TouchEvent<HTMLButtonElement>) => {
    onTouchCancel?.(e);
    if (isMobile) resetPressed();
  };

  const renderContent = () => {
    const textClasses = variantStyles.innerTextClass;
    if (typeof children === "string") {
      return <PContent className={textClasses}>{children}</PContent>;
    }
    return (
      <div className={`${textClasses} flex items-center justify-center gap-2`}>
        {children}
      </div>
    );
  };

  const buttonWidth = expandWidth ? "auto" : `${size.width}px`;
  const buttonHeight = `${size.height}px`;

  const buttonStyle: React.CSSProperties = {
    width: buttonWidth,
    height: buttonHeight,
    minWidth: expandWidth ? "120px" : "auto",
    whiteSpace: expandWidth ? "nowrap" : "normal",
  };
  if (variantStyles.hasScale) {
    buttonStyle.transform = pressed ? "scale(0.95)" : "scale(1)";
    buttonStyle.transition = "transform 0.2s ease";
  }

  return (
    <button
      type={type}
      style={{ ...buttonStyle, ...style }}
      className={classNames(variantStyles.baseClasses, className)}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchCancel}
      {...props}
    >
      <span className="relative z-10">{renderContent()}</span>
    </button>
  );
};

export default Button;
